import { useState } from "react";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { isValidMinProfit } from "@/lib/settings/min-profit";

const SLIDER_MAX_ADA = 100;

interface MinProfitControlProps {
    id: string;
    value: number;
    onChange: (ada: number) => void;
}

/** A slider for the common range plus a number input for anything, both bound to one ADA value. */
export function MinProfitControl({ id, value, onChange }: MinProfitControlProps) {
    // The typed text is kept apart from `value` so a half-typed "" or "4." is not snapped back;
    // it is dropped as soon as `value` changes from elsewhere (the slider, back navigation).
    const [draft, setDraft] = useState({ of: value, text: String(value) });
    const text = draft.of === value ? draft.text : String(value);

    const type = (next: string) => {
        const ada = Number(next);
        const valid = next.trim() !== "" && isValidMinProfit(ada);
        setDraft({ of: valid ? ada : value, text: next });
        if (valid && ada !== value) onChange(ada);
    };

    return (
        <div className="space-y-2">
            <Label htmlFor={id}>Minimum profit (ADA)</Label>
            <div className="flex items-center gap-3">
                <input
                    type="range"
                    aria-label="Minimum profit slider"
                    min={0}
                    max={Math.max(SLIDER_MAX_ADA, value)}
                    step={1}
                    value={value}
                    onChange={(event) => onChange(event.target.valueAsNumber)}
                    className="h-11 min-w-0 flex-1 accent-primary md:h-10"
                />
                <Input
                    id={id}
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="any"
                    value={text}
                    onChange={(event) => type(event.target.value)}
                    className="w-24 shrink-0"
                />
            </div>
        </div>
    );
}
